import { useCallback, useMemo, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FilterBar } from './FilterBar';
import { StationsTable } from './StationsTable';
import { StationsTableSkeleton } from './StationsTableSkeleton';
import { MobileStationCard } from './MobileStationCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { ListPageLayout } from '../components/ListPageLayout';
import { StationsFilterState, StationV2 } from '../../../types/dashboardV2';
import { ROUTES } from '../../../constants/routes';
import { useStationsQuery } from '../../../hooks/useDashboardQueries';
import { useListPage } from '../../../hooks/useListPage';
import { useActiveFilterCount } from '../../../hooks/useActiveFilterCount';
import { StationStatus } from '../../../shared/enums/status';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import axiosInstance from '../../../config/axiosInstance';

const MobileFilterSheet = lazy(() => import('./MobileFilterSheet'));

import type { MobileStationsFilterState } from '../../../types/mobileFilterSheetTypes';

const MAX_PRICE_RANGE = 100;

const INITIAL_FILTERS: StationsFilterState = {
  search: '',
  status: '',
  priceRange: MAX_PRICE_RANGE,
  connectorType: '',
  maxPower: 0,
  openNow: false,
  organizationId: '',
};

export const StationsV2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    isFilterSheetOpen,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh: handleRefresh,
    openFilterSheet,
    closeFilterSheet,
  } = useListPage<StationsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.STATIONS,
    urlSync: true,
    numericFilterKeys: ['priceRange', 'maxPower'],
  });

  const {
    data,
    isLoading: loading,
    isFetching,
    error,
  } = useStationsQuery({ page: page + 1, pageSize: rowsPerPage, filters: queryFilters });

  const stations = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;

  const handleOrganizationChange = useCallback(
    (organizationId: string | undefined) => handleFilterChange({ organizationId: organizationId ?? '' }),
    [handleFilterChange],
  );

  const handleAddStation = useCallback(
    () => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.CREATE_STATION}`),
    [navigate],
  );

  const handleQrClick = useCallback(async (station: StationV2) => {
    try {
      const response = await axiosInstance.get(`/qrcode/${station.idTag}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qrcode-${station.idTag}.png`);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading QR code:', err);
    }
  }, []);

  const handleCardClick = useCallback(
    (station: StationV2) =>
      navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_STATIONS}/${station.id}`),
    [navigate],
  );

  // Backend only filters by search/address. Status, priceRange, connectorType,
  // maxPower, and openNow are applied client-side to the current page,
  // so counts may differ from total.
  const filteredStations = useMemo(() => {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    const isOpen = (s: StationV2): boolean => {
      if (s.aroundTheClock) return true;
      if (!s.startWorkingTime || !s.endWorkingTime) return false;
      const start = new Date(s.startWorkingTime);
      const end = new Date(s.endWorkingTime);
      const startMin = start.getHours() * 60 + start.getMinutes();
      const endMin = end.getHours() * 60 + end.getMinutes();
      // Handle overnight ranges (e.g., 22:00–06:00).
      return endMin > startMin
        ? nowMin >= startMin && nowMin < endMin
        : nowMin >= startMin || nowMin < endMin;
    };

    return stations.filter((station) => {
      if (filters.status && station.status !== filters.status) return false;
      if (station.price > filters.priceRange) return false;
      if (filters.connectorType && !station.connectorTypes?.includes(filters.connectorType)) {
        return false;
      }
      if (filters.maxPower > 0 && (station.power ?? 0) > filters.maxPower) return false;
      if (filters.openNow && !isOpen(station)) return false;
      return true;
    });
  }, [stations, filters.status, filters.priceRange, filters.connectorType, filters.maxPower, filters.openNow]);

  const hasClientFilter =
    Boolean(filters.status) ||
    filters.priceRange < MAX_PRICE_RANGE ||
    Boolean(filters.connectorType) ||
    filters.maxPower > 0 ||
    filters.openNow;

  const handleApplyMobileFilters = useCallback(
    (mobile: MobileStationsFilterState) => {
      handleFilterChange({
        status: mobile.status ?? '',
        priceRange: mobile.priceRange,
        connectorType: mobile.connectorType ?? '',
        maxPower: mobile.maxPower,
        openNow: mobile.openNow,
      });
    },
    [handleFilterChange],
  );

  const mobileFiltersForSheet: MobileStationsFilterState = useMemo(
    () => ({
      status: (filters.status as StationStatus) || null,
      priceRange: filters.priceRange,
      connectorType: (filters.connectorType as never) || null,
      maxPower: filters.maxPower,
      openNow: filters.openNow,
    }),
    [filters.status, filters.priceRange, filters.connectorType, filters.maxPower, filters.openNow],
  );

  const errorMessage = error instanceof Error ? error.message : t('dashboard.stations.errorLoading');
  const activeFilterCount = useActiveFilterCount(filters, INITIAL_FILTERS);

  return (
    <ListPageLayout
      filterBar={
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          onAddStation={handleAddStation}
          organizationId={filters.organizationId || undefined}
          onOrganizationChange={handleOrganizationChange}
        />
      }
      showLoading={loading || isFetching}
      isLoading={loading}
      hasError={Boolean(error)}
      errorMessage={errorMessage}
      onRetry={handleRefresh}
      countText={
        hasClientFilter
          ? t('dashboard.stations.displayingFiltered', { count: filteredStations.length })
          : t('dashboard.stations.displayingOfTotal', { count: filteredStations.length, total })
      }
      itemCount={filteredStations.length}
      emptyState={{ icon: 'ev_station', message: t('dashboard.stations.noStations') }}
      table={
        <StationsTable
          stations={filteredStations}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onQrClick={handleQrClick}
        />
      }
      tableSkeleton={<StationsTableSkeleton />}
      mobileTitle={t('dashboard.sidebar.stations')}
      mobileHeaderAction={
        <button type="button" className="mobile-add-btn" onClick={handleAddStation}>
          <span className="material-symbols-outlined" aria-hidden="true">add</span>
          <span>{t('dashboard.stations.addStation')}</span>
        </button>
      }
      mobileCountText={t('dashboard.stations.allStationsWithCount', { count: filteredStations.length })}
      search={{
        value: filters.search,
        onChange: (search) => handleFilterChange({ search }),
        placeholder: t('dashboard.stations.searchPlaceholder'),
        inputProps: { 'aria-label': t('dashboard.stations.searchAria') },
      }}
      mobileCards={filteredStations.map((station) => (
        <MobileStationCard
          key={station.id}
          station={station}
          onQrClick={handleQrClick}
          onCardClick={handleCardClick}
        />
      ))}
      mobileSkeleton={<MobileCardSkeleton />}
      pagination={{
        total,
        page,
        rowsPerPage,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
      }}
      filterButton={{ activeCount: activeFilterCount, onOpen: openFilterSheet }}
      isFilterSheetOpen={isFilterSheetOpen}
      filterSheet={
        <MobileFilterSheet
          isOpen={isFilterSheetOpen}
          onClose={closeFilterSheet}
          onApply={handleApplyMobileFilters}
          initialFilters={mobileFiltersForSheet}
        />
      }
    />
  );
};

export default StationsV2;
